import React from 'react';

// Components
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Styling
import styled, { withTheme } from 'styled-components';

import 'react-datepicker/dist/react-datepicker.css';

export const Label = styled.label`
  font-size: 11px;
  font-weight: normal;
  color: ${(props) => props.theme.primary200};
  margin: 0 0 5px;
  position: relative;
  &:after {
    display: ${({ mandatory }) => (mandatory ? 'block' : 'none')};
    position: absolute;
    content: '*';
    color: ${(props) => props.theme.errorLight};
    right: -8px;
    top: 0;
  }
`;

const DatePickerWrapper = styled.div`
  position: relative;
  z-index: 1;
  min-width: 200px;

  input {
    width: 100%;
    height: 30px;
    box-sizing: border-box;
    padding: 8px 15px;
    border-radius: 3px;
    border: ${({ theme }) => `1px solid ${theme.gray300}`};
    transition: all 100ms;
    background-color: ${(props) => props.theme.grayWhiteOff};
    font-size: 11px;
    font-weight: 600;
    color: ${(props) => props.theme.primary200};
    outline: transparent;

    &:hover {
      cursor: pointer;
    }

    &::placeholder {
      color: ${(props) => props.theme.primary200};
      font-size: 11px;
    }

    &:disabled {
      background: ${(props) => props.theme.showcaseWhite};
      color: ${(props) => props.theme.primary100};
    }

    &:focus {
      border: 1px solid ${(props) => props.theme.gray300};
    }
  }

  .react-datepicker-wrapper {
    display: block;
    width: 100%;
  }

  .react-datepicker__close-icon {
    right: 2px;
  }

  .react-datepicker__close-icon::after {
    width: 15px;
    height: 15px;
    font-size: 1rem;
    font-weight: bold;
    line-height: 0.6;
    box-sizing: border-box;
    background: transparent;
    color: ${(props) => props.theme.primary100};
  }

  .react-datepicker__close-icon::before {
    display: block;
    content: '';
    position: absolute;
    width: 15px;
    height: 15px;
    bottom: 6px;
    right: 4px;
    border-radius: 50%;
    border: 2px solid ${(props) => props.theme.primary100};
  }

  ${({ styles }) => styles}
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  padding: 15px;
  border: 1px solid ${({ theme }) => theme.gray300};
  border-radius: 3px;
  font-size: 1rem;
  box-sizing: border-box;
  text-align: center;
`;

const Icon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  color: ${(props) => props.theme.gray400};
  pointer-events: none;
`;

const IconWrapper = styled.div`
  position: relative;
  div {
    z-index: 2;
  }
`;

const CustomDatePicker = ({
  label,
  value,
  required = false,
  language,
  onChange,
  name,
  setDateUnix,
  isClearable = false,
  showTimeSelect = false,
  enableMaxDate = false,
  wrapperStyles,
  ...props
}) => {
  const placeholderText = 'DD/MM/YYYY';
  let dateFormat = 'dd/MM/yyyy';
  const timeFormat = 'HH:mm';

  if (enableMaxDate) {
    props.maxDate = new Date().getTime();
  }
  if (showTimeSelect) {
    dateFormat = 'dd/MM/yyyy - HH:mm';
  }

  return (
    <DatePickerWrapper styles={wrapperStyles}>
      {!!label && (
        <Label htmlFor={name} mandatory={required}>
          {label}
        </Label>
      )}
      <IconWrapper>
        <StyledDatePicker
          {...props}
          selected={value ? new Date(value) : null || null}
          onChange={(val) => {
            if (val instanceof Date) {
              onChange(val.getTime());
              return val.getTime();
            } else if (val instanceof Array) {
              onChange(val);
              return val;
            } else {
              onChange(null);
            }
          }}
          onKeyDown={(event) => event.preventDefault()}
          placeholderText={placeholderText}
          autoComplete="off"
          showTimeSelect={showTimeSelect}
          dateFormat={dateFormat}
          timeFormat={timeFormat}
          required={required}
          id={name}
          isClearable={isClearable}
        />
        {!value ? <Icon icon={['far', 'calendar-day']} size="1x" /> : null}
      </IconWrapper>
    </DatePickerWrapper>
  );
};

export default withTheme(CustomDatePicker);
